import { motion } from 'motion/react'
import { useCallback, useContext, useEffect } from 'react'
import { Checkbox } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'
import MdiRefresh from '~icons/mdi/refresh'
import { UIButton } from 'src/components/ui/Button'
import {
  executeSetPreferences,
  updateGetMyPreferences,
} from 'src/services/apollo/preference'
import { getMediaUrl } from 'src/utils/media'
import { track } from 'src/utils/tracker'
import { getOnboardingLocalizedName } from '../helper'
import { ImageWithLoadAnimation } from '../ImageWithLoadAnimation'
import { NewUserCharacterOptionsContext } from '../NewUserCharacterOptionsContext'
import { NewUserOnboardingContext } from '../NewUserOnboardingContext'
import { useNewUserOnboardingFormContext } from '../newUserOnboardingForm'
import {
  NewUserOnboardingFormStep,
  NewUserOnboardingSkipButton,
} from '../NewUserOnboardingFormStep'

const MotionCheckbox = motion.create(Checkbox)

function CharacterSelector(props: {
  onChange: (value: string[]) => void
  value?: string[]
  disabled: boolean
}) {
  const { t, i18n } = useTranslation(['common', 'user'])
  const selectedCharacters = props.value ?? []

  const { characterOptions, initOptions, resetOptions, addMoreOptions } =
    useContext(NewUserCharacterOptionsContext)
  useEffect(() => {
    initOptions()
  }, [])

  return (
    <>
      <div className="mt-4 mb-3 flex justify-center">
        <UIButton
          disabled={props.disabled}
          variant="ghost"
          className="px-2.5 h-8"
          onPress={resetOptions}
          icon={<MdiRefresh />}
        >
          {t('user:onboarding.character.reroll.label')}
        </UIButton>
      </div>
      <div className="grid grid-cols-3 min-[512px]:grid-cols-4 gap-x-4 gap-y-3">
        {characterOptions.map(character => {
          const isSelected = selectedCharacters.includes(character.tackId)
          const name = getOnboardingLocalizedName(character.names, i18n)
          return (
            <MotionCheckbox
              isDisabled={props.disabled}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              layout
              transition={{
                default: { duration: 0.2, ease: 'easeOut' },
                opacity: { duration: 0.4 },
              }}
              key={character.key}
              className="flex flex-col items-center gap-2 group"
              isSelected={isSelected}
              onChange={isSelected => {
                if (isSelected) {
                  props.onChange([...selectedCharacters, character.tackId])
                  addMoreOptions(character)
                } else {
                  props.onChange(
                    selectedCharacters.filter(
                      item => item !== character.tackId,
                    ),
                  )
                }
              }}
            >
              <ImageWithLoadAnimation
                wrapperClassName={twMerge(
                  'size-16 rounded-full ring-2 ring-offset-2',
                  isSelected
                    ? 'ring-theme-primary'
                    : 'ring-neutral-200 dark:ring-neutral-600 group-hover:ring-theme-primary/50 group-hover:dark:ring-theme-primary/60 group-focus-visible:ring-theme-primary/50 group-focus-visible:dark:ring-theme-primary/60',
                )}
                src={getMediaUrl(character.mediaId, 'thumbnail')}
                alt={name}
              />
              <p className="text-center line-clamp-2 break-words h-10 w-full text-sm">
                {name}
              </p>
            </MotionCheckbox>
          )
        })}
      </div>
    </>
  )
}

export function NewUserCharacterStep() {
  const { t } = useTranslation(['common', 'user'])
  const form = useNewUserOnboardingFormContext()
  const { characters } = form.values
  const submitCategoriesAndCharacters = useCallback(async () => {
    const { categories, characters } = form.getValues()
    const tackIds = characters ?? []
    const preferences = {
      interestedIn: categories,
      favoritedCharacterTacks: tackIds,
    }
    await executeSetPreferences({ value: preferences })
    updateGetMyPreferences(preferences)
    track('started_choice', {
      interestedIn: categories,
      favoritedCharacterTacks: tackIds,
    })
  }, [form.getValues])
  const { actionBusy } = useContext(NewUserOnboardingContext)
  return (
    <NewUserOnboardingFormStep
      disableNext={(characters?.length ?? 0) === 0}
      action={submitCategoriesAndCharacters}
    >
      <h2 className="text-xl font-bold mb-4">
        <NewUserOnboardingSkipButton />
        {t('user:onboarding.character.title')}
      </h2>
      <p className="text-neutral-600 dark:text-neutral-400">
        {t('user:onboarding.character.info')}
      </p>
      <CharacterSelector
        disabled={actionBusy}
        {...form.getInputProps('characters')}
      />
    </NewUserOnboardingFormStep>
  )
}
